import React, { useState, useContext, useEffect } from 'react';

import { useForm } from 'react-hook-form';
import { Link, useHistory } from 'react-router-dom';

import { PreAuthPage } from 'containers/PreAuthPage';

import { Button } from 'components/Button';
import { Loading } from 'components/Loading';
import { Input } from 'components/Form/subcomponents/Input';
import { ModalGeneric } from 'components/ModalGeneric';

import { AuthContext } from 'context/AuthContext';

import { AuthService } from 'services/main';

import leankeepLogo from 'assets/icons/leankeep/leankeep-facility-logo.svg';
import errorIcon from 'assets/icons/warning-hexa-o-icon.svg';
import iconError503 from 'assets/icons/icon_error.svg';
import * as Sentry from '@sentry/react';
import {
  StyledWelcome,
  StyledForm,
  StyledFormGroup,
  StyledFormLogin,
  StyledFormFooter,
  StyledForgetPasswordLink,
  StyledStayConnectedCheckbox,
  StyledTooltip,
  StyledModalBody,
  StyledModalButton,
  StyledP,
  Container,
  Linha,
  Texto,
  SSOLoginButton,
} from './styled';
import { parseJwt } from 'utils/parse';
//import { getLocalAuthStorage } from 'utils/localStorage';
import { createGenericErrorToast } from 'utils/createToast';
import { Toast } from 'components/Toast';
import { useCookies } from 'react-cookie';
import { URLS } from 'constants/urls';

const Login = () => {
  const [toastList, setToastList] = useState([]);
  const [isActive, setIsActive] = useState(true);
  const [isActiveForce, setIsActiveForce] = useState(true);
  const { register, handleSubmit, setValue, getValues, setError, errors, watch } = useForm();
  //const authStorage = getLocalAuthStorage();
  const setCookie = useCookies(['authToken', 'refreshToken', 'lastLoginRoute'])[1];
  const { push } = useHistory();
  const { authContext, setAuthContext } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentModalInfo, setCurrentModalInfo] = useState('errorPermission');
  const modalInfos = {
    errorPermission: {
      icon: errorIcon,
      title: 'Acesso exclusivo por login',
      text: (
        <>
          <p>Se outro dispositivo estiver conectado, ele será desconectado.</p>
        </>
      ),
    },
    errorServiceUnavailable: {
      icon: iconError503,
      title: 'Sua rede está com dificuldade de acessar o Leankeep',
      text: (
        <>
          <p>
            Entre em contato com o responsável pela rede e
            <br />
            verifique se o acesso ao *.lkp.app.br está liberado.
          </p>
        </>
      ),
    },
  };

  const handleLogin = async ({ login, password, stayLogged, expireCurrentSession }) => {
    setLoading(true);

    const response = await AuthService.login({
      login,
      password,
      withRefreshToken: stayLogged,
      stayConnected: stayLogged,
      expireCurrentSession: expireCurrentSession,
    });
    const { data } = response;
    if (response.status === 200) {
      await onLogin(response);
    }

    if (response.status === 401 && data.hasActiveSession) {
      // login and password are correct but there is an active session
      setCurrentModalInfo('errorPermission');
      setIsModalOpen(true);
    }

    if (data.message === 'Invalid user access! It may have been suspended or inactivated.' && !data.hasActiveSession) {
      // login and password are incorrect
      setError('login', {
        type: 'unauthorized',
        message: 'Usuario invalido',
      });

      setError('password', {
        type: 'unauthorized',
        message: 'Senha incorreta',
      });
      setValue('login', '');
      setValue('password', '');
      setIsActive(true);
    }

    if (data.authtoken && response.statusCode === 403) {
      // user with local network lock
      setCurrentModalInfo('errorServiceUnavailable');
      setIsModalOpen(true);
    }
    setLoading(false);
  };

  const onSubmit = async ({ login, password, stayLogged }) => {
    setIsActive(false);
    return await handleLogin({ login, password, stayLogged });
  };
  const onLogin = async res => {
    const {
      data: { user, ...data },
    } = res;

    try {
      setCookie('authToken', data.authToken.token, { path: '/', domain: URLS.cookieDomain });
      setCookie('refreshToken', data?.authToken?.refreshToken, {
        path: '/',
        domain: URLS.cookieDomain,
      });
      const { utyp, supplier } = parseJwt(data.authToken.token);
      const { isSuper, isAdmin, isOperationalUser, isCommonUser } = {
        isSuper: parseInt(utyp) === 1,
        isAdmin: parseInt(utyp) === 2,
        isOperationalUser: parseInt(utyp) === 3,
        isCommonUser: parseInt(utyp) === 4,
      };
      const permissionType = isSuper ? 'super' : isAdmin ? 'admin' : 'user';
      const userType = isSuper
        ? 'Super-Usuário'
        : isAdmin
        ? 'Administrador'
        : isOperationalUser
        ? 'Operacional'
        : isCommonUser
        ? 'Chamado'
        : createGenericErrorToast(setToastList);
      const userResponse = await AuthService.getUser({ id: user?.id, token: data.authToken.token });
      user.fullName = userResponse.data.nome;
      const signUpDateUnix = Math.floor(new Date(userResponse.data.dataCadastro).getTime() / 1000);
      user.signUpDate = signUpDateUnix;
      if (supplier) {
        setCookie('providers', { id: supplier, name: user?.fullName }, { path: '/', domain: URLS.cookieDomain });
      }
      setCookie(
        'chamadosAccess',
        { chamado: userResponse?.data?.filtrosDefault, userId: userResponse?.data?.usuario },
        { path: '/', domain: URLS.cookieDomain },
      );
      setCookie('lastLoginRoute', '/login', { path: '/', domain: URLS.cookieDomain });
      setAuthContext({
        ...authContext,
        isAuthenticated: true,
        statusCode: true,
        token: data?.authToken?.token,
        refreshToken: data?.authToken?.refreshToken,
        expiresIn: data?.authToken?.expiresIn,
        user: {
          id: user?.id,
          name: user?.name,
          permissionType: permissionType,
          type: userType,
          fullName: user?.fullName,
          signUpUnixDate: user?.signUpDate,
        },
      });
    } catch (e) {
      setIsActive(true);
      console.error(`userResponse err`);
      console.error(e);
    }
  };
  // TODO REFACTOR
  // login despite active session
  const forceLogin = async () => {
    setIsActiveForce(false);
    const login = getValues('login');
    const password = getValues('password');
    const stayLogged = getValues('stayLogged');
    try {
      setLoading(true);
      // expire session = true
      const response = await AuthService.login({
        login,
        password,
        withRefreshToken: stayLogged,
        stayConnected: stayLogged,
        expireCurrentSession: true,
      });
      setIsModalOpen(false);
      await onLogin(response);
    } catch (e) {
      setIsActiveForce(true);
      console.error(e);
    } finally {
      setLoading(false);
      setIsActiveForce(true);
    }
  };
  const handleAccessWithOtherUser = () => {
    setValue('login', '');
    setValue('password', '');
    setIsModalOpen(false);
    setIsActive(true);
  };
  useEffect(() => {
    if (authContext?.isAuthenticated && authContext?.token) {
      const { utyp, comps } = parseJwt(authContext?.token);
      const companies = JSON.parse(comps);

      if (companies[0]?.Units?.[0] && !companies[0]?.MultipleAccess) {
        setCookie('units', { id: companies[0]?.Units?.[0], name: '' }, { path: '/', domain: URLS.cookieDomain });
      }
      if (companies[0]?.Id) {
        setCookie('companies', { id: companies[0]?.Id, name: '' }, { path: '/', domain: URLS.cookieDomain });
      }
      if (parseInt(utyp) === 4) {
        Sentry.captureMessage('Login Chamado', {
          tags: { Empresa: companies[0]?.Id },
        });
        push('/portal-do-cliente');
        return;
      }

      if (process.env.NODE_ENV !== 'development') {
        //   //redirect only in production and homolog
        //   window.location.href = `${process.env.REACT_APP_LEGADO}/Security/SignIn.aspx?${authStorage.token}`;
        window.location.href = `${process.env.REACT_APP_LEGADO}/Security/SignIn.aspx`;
        return null;
      } else {
        push('/home');
        return;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [push, authContext]);
  const loginValue = watch('login');
  const [showSSO, setShowSSO] = useState(false);
  useEffect(() => {
    const email = loginValue || '';
    const isValidEmail = email.endsWith('@grupoboticario.com.br');
    setShowSSO(!!loginValue && isValidEmail);
  }, [loginValue]);
  const handleSSOLogin = event => {
    event.preventDefault();
    const url = `${process.env.REACT_APP_AUTH_API}/v1/AuthSSO?companyId=${process.env.REACT_APP_SSO_COMPANY_ID}`;

    window.open(url, 'SSOLoginWindow', 'width=500,height=600,left=200,top=100');
  };
  useEffect(() => {
    const handleToken = (authToken, refreshToken, expiresIn) => {
      try {
        const payload = parseJwt(authToken);
        const comps = JSON.parse(payload.comps);

        const fakeResponse = {
          data: {
            user: {
              id: payload.nameid,
              name: payload.unique_name,
            },
            company: {
              id: comps[0]?.Id,
            },
            unit: {
              id: comps[0]?.Units[0],
              companyId: comps[0]?.Id,
            },
            authToken: {
              token: authToken,
              refreshToken: refreshToken,
              expiresIn: expiresIn,
            },
          },
        };

        onLogin(fakeResponse);
        //console.log('🔐 Token recebido via postMessage:', fakeResponse);
      } catch (error) {
        console.error('Erro ao processar token recebido via postMessage:', error);
      }
    };

    const messageHandler = event => {
      const allowedOrigin = process.env.REACT_APP_SSO_REDIRECT; // Altere para seu domínio real

      if (event.origin !== allowedOrigin) return;
      if (event.data?.type === 'SSO_TOKEN') {
        const { token, refreshToken, expiresIn } = event.data;
        handleToken(token, refreshToken, expiresIn);
      }
    };

    window.addEventListener('message', messageHandler);

    return () => {
      window.removeEventListener('message', messageHandler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <PreAuthPage>
      <StyledForm onSubmit={handleSubmit(onSubmit)}>
        <img src={leankeepLogo} alt="Leankeep" />

        <StyledWelcome>
          <h3>Bom te receber de volta.</h3>
          <span>Informe seus dados abaixo</span>
        </StyledWelcome>

        <StyledFormLogin>
          <StyledFormGroup label="Login">
            <Input
              type="text"
              name="login"
              size="large"
              tabIndex={0}
              //onChange={handleLoginChange}
              error={errors.login !== undefined}
              errormessage={errors.login && errors.login.message}
              ref={register({
                required: 'Este campo é obrigatório.',
              })}
            />
          </StyledFormGroup>
          {!showSSO && (
            <>
              <StyledFormGroup label="Senha" isPassword>
                <StyledForgetPasswordLink>
                  <Link to="/conta/recuperar" tabIndex={-1}>
                    Esqueceu sua senha?
                  </Link>
                </StyledForgetPasswordLink>
                <Input
                  type="password"
                  name="password"
                  size="large"
                  tabIndex={0}
                  isPassword
                  error={errors.password !== undefined}
                  errormessage={errors.password && errors.password.message}
                  ref={register({
                    required: 'Este campo é obrigatório.',
                  })}
                />
              </StyledFormGroup>
              <StyledFormFooter>
                <StyledStayConnectedCheckbox
                  name="stayLogged"
                  text="Mantenha-me conectado(a)"
                  defaultChecked={true}
                  ref={register}
                />
                <StyledTooltip text="Sua conta estará sempre logada. Desmarque se este computador for compartilhado, por segurança." />

                <Button id="loginButton" type="submit" disabled={!isActive}>
                  Entrar
                </Button>
              </StyledFormFooter>
            </>
          )}
          {showSSO && (
            <StyledFormGroup>
              <Container>
                <Linha />
                <Texto>Ou continue com:</Texto>
                <Linha />
              </Container>
              <SSOLoginButton onClick={handleSSOLogin}>Login com SSO</SSOLoginButton>
            </StyledFormGroup>
          )}
        </StyledFormLogin>
      </StyledForm>
      <Toast toastList={toastList} autoDelete dismissTime={7000} />

      <>
        <ModalGeneric
          size={{ height: '296px' }}
          open={isModalOpen}
          closeModal={() => {
            setIsModalOpen(false);
            setIsActive(true);
          }}
        >
          <StyledModalBody>
            <img src={modalInfos[currentModalInfo].icon} alt="usuario invalido" />
            <h6>{modalInfos[currentModalInfo].title}</h6>
            {currentModalInfo === 'errorPermission' ? (
              <>
                {modalInfos[currentModalInfo].text}
                <StyledModalButton>
                  <Button type="button" theme="primary" onClick={() => forceLogin()} disabled={!isActiveForce}>
                    Ok
                  </Button>
                </StyledModalButton>
              </>
            ) : (
              <>
                <StyledP>{modalInfos[currentModalInfo].text}</StyledP>
                <Button type="button" onClick={() => handleAccessWithOtherUser()}>
                  Voltar à página inicial
                </Button>
              </>
            )}
          </StyledModalBody>
        </ModalGeneric>
        {loading && <Loading />}
      </>
    </PreAuthPage>
  );
};

export { Login };
